import { useCallback } from "react";
import { fetchPayCycles, setPayCycleDisabled, createPayCycle } from "../api/AdminManager/PayCycle/PayCycleApi";
import { createPayrollRun, generatePayslipsForPayrollRun } from "../api/AdminManager/Payroll/PayrollApi";
import type { PayCycle, PayCycleSettings, PayrollExclusion } from "../types/PayCycleContext.types";


import { logger } from '../utils/logger';
export function usePayCycleHelpers(
  setPayCycles: React.Dispatch<React.SetStateAction<PayCycle[]>>,
  setCurrentCycle: React.Dispatch<React.SetStateAction<PayCycle | null>>,
  setCycleEmployees: React.Dispatch<React.SetStateAction<any[]>>,
  setExcludedEmployees: React.Dispatch<React.SetStateAction<PayrollExclusion[]>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) {
  const refreshData = useCallback(async () => {
    setLoading(true);
    try {
      const apiRows = await fetchPayCycles();
      const normalized: PayCycle[] = apiRows.map((row) => ({
        id: String(row.pay_cycle_id),
        cycleName: row.cycle_name,
        frequency: row.frequency || 'weekly',
        lastPayDate: row.last_pay_date,
        upcomingPayDate: row.upcoming_pay_date,
        status: row.is_disabled ? 'paused' : 'active',
        exclusions: row.exclusions.length,
        exclusionItems: row.exclusions,
        totalEmployees: 0,
        processedEmployees: 0,
        netPayroll: 0,
        action: null,
        isDisabled: row.is_disabled,
      }));

      setPayCycles(normalized);
      setCurrentCycle(null);
      setCycleEmployees([]);
      setExcludedEmployees([]);
    } catch (error) {
      console.error('Failed to refresh pay cycle data:', error);
      setPayCycles([]);
      setCurrentCycle(null);
    } finally {
      setLoading(false);
    }
  }, [setPayCycles, setCurrentCycle, setCycleEmployees, setExcludedEmployees, setLoading]);

  const setCycleDisabledStatus = useCallback(async (cycleId: string, disabled: boolean) => {
    setPayCycles((prev) =>
      prev.map((cycle) => {
        if (cycle.id !== cycleId) return cycle;
        return {
          ...cycle,
          isDisabled: disabled,
          status: disabled ? 'paused' : 'active',
        };
      })
    );

    setCurrentCycle((cycle) => {
      if (!cycle || cycle.id !== cycleId) return cycle;
      return {
        ...cycle,
        isDisabled: disabled,
        status: disabled ? 'paused' : 'active',
      };
    });

    try {
      await setPayCycleDisabled(Number(cycleId), disabled);
    } catch (error) {
      console.error('Failed to update pay cycle status:', error);
    }
  }, [setPayCycles, setCurrentCycle]);

  const createCycle = useCallback(async (settings: PayCycleSettings & { exclude_uif?: boolean; exclude_sdl?: boolean; exclude_tax_cert?: boolean; frequency?: string }) => {
    setLoading(true);
    try {
      await createPayCycle({
        cycle_name: settings.cycleName,
        start_date: settings.startDate,
        frequency: settings.frequency || 'weekly',
        exclude_uif: settings.exclude_uif || false,
        exclude_sdl: settings.exclude_sdl || false,
        prorata_bonus: settings.prorata_bonus || false,
        exclude_tax_cert: settings.exclude_tax_cert || false,
      });
      await refreshData();
    } catch (error) {
      console.error('Failed to create pay cycle:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, refreshData]);

  const updateCycle = useCallback(async (cycleId: string, updates: Partial<PayCycle>) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      logger.debug(`Updating cycle ${cycleId}:`, updates);
      await refreshData();
    } catch (error) {
      console.error('Failed to update pay cycle:', error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, refreshData]);

  const deleteCycle = useCallback(async (cycleId: string) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      logger.debug(`Deleting cycle ${cycleId}`);
      await refreshData();
    } catch (error) {
      console.error('Failed to delete pay cycle:', error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, refreshData]);

  const duplicateCycle = useCallback(async (cycleId: string) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      logger.debug(`Duplicating cycle ${cycleId}`);
      await refreshData();
    } catch (error) {
      console.error('Failed to duplicate pay cycle:', error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, refreshData]);

  const processPayroll = useCallback(async (cycleId: string) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      logger.debug(`Processing payroll for cycle ${cycleId}`);
      await refreshData();
    } catch (error) {
      console.error('Failed to process payroll:', error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, refreshData]);

  const approvePayroll = useCallback(async (cycleId: string) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      logger.debug(`Approving payroll for cycle ${cycleId}`);
      await refreshData();
    } catch (error) {
      console.error('Failed to approve payroll:', error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, refreshData]);

  const generatePayslips = useCallback(async (cycleId: string) => {
    setLoading(true);
    try {
      // Step 1: Create payroll run (backend will auto-calculate pay date if not provided)
      const payrollRunResponse = await createPayrollRun(Number(cycleId));
      const payrollRunId = payrollRunResponse.payrollRun?.payroll_run_id;

      if (!payrollRunId) {
        throw new Error('Failed to create payroll run');
      }

      // Step 2: Generate payslips for the payroll run
      const payslipsResponse = await generatePayslipsForPayrollRun(payrollRunId);

      logger.debug(`Generated ${payslipsResponse.payslips_generated} payslips for cycle ${cycleId}`);
      
      await refreshData();
    } catch (error) {
      console.error('Failed to generate payslips:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, refreshData]);

  const addExclusion = useCallback(async (exclusion: Omit<PayrollExclusion, 'id'>) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      logger.debug('Adding exclusion:', exclusion);
      await refreshData();
    } catch (error) {
      console.error('Failed to add exclusion:', error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, refreshData]);

  const removeExclusion = useCallback(async (exclusionId: string) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      logger.debug(`Removing exclusion ${exclusionId}`);
      await refreshData();
    } catch (error) {
      console.error('Failed to remove exclusion:', error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, refreshData]);

  return {
    refreshData,
    setCycleDisabledStatus,
    createCycle,
    updateCycle,
    deleteCycle,
    duplicateCycle,
    processPayroll,
    approvePayroll,
    generatePayslips,
    addExclusion,
    removeExclusion,
  };
}
